import type { AxiosError } from "axios";
import { worktimeApi } from "@wkt/api/worktime";
import type { DocumentsInfoDTO } from "@wkt/api/documents/documentsDTO";
import type {
  Department,
  Device,
  Employee,
  Project,
  SessionInformation,
  WorkPlace,
} from "@wkt/api/gwt/gwtDTO";
import type { FeatureMessageDTO, TermInfoDTO } from "@wkt/api/user/userDTO";

type SuccessCallback<T = unknown> = (data: T) => void;
type ErrorCallback = (error: AxiosError) => void;

export interface SessionInfoParamsDTO {
  module: string;
  timezone: string;
  date: {
    year: number;
    month: number;
    day: number;
  };
}

interface ResultListDTO<T> {
  entities?: T[] | null;
}

export class UserAPI {
  getSessionInformation(
    params: SessionInfoParamsDTO,
    onSuccess: SuccessCallback<SessionInformation>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericJsonPost("user/sessionInformation", params, onSuccess, onError);
  }

  getEmployeeWithKioskCheck(
    onSuccess: SuccessCallback<Employee>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet("user/employeeWithKioskCheck", onSuccess, onError);
  }

  getDocumentsInfo(
    onSuccess: SuccessCallback<DocumentsInfoDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet("user/documents-info", onSuccess, onError);
  }

  clearEmployeeBadge(
    badgeType: number,
    onSuccess?: SuccessCallback<void>,
    onError?: ErrorCallback,
  ): void {
    worktimeApi.genericPut(
      `user/clearEmployeeBadge/${badgeType}`,
      null,
      onSuccess,
      onError,
    );
  }

  getFeatureMessages(
    module: string,
    onSuccess: SuccessCallback<FeatureMessageDTO[]>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet(
      `user/getAllFeatureMessages/${module}`,
      onSuccess,
      onError,
    );
  }

  getAcceptedTerms(
    onSuccess: SuccessCallback<TermInfoDTO[]>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet("user/getAcceptedTerms", onSuccess, onError);
  }

  reportIncidence(
    message: string,
    onSuccess: SuccessCallback<boolean>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost("user/reportEmail", message, onSuccess, onError);
  }

  acceptTerm(
    termId: number,
    onSuccess?: SuccessCallback<void>,
    onError?: ErrorCallback,
  ): void {
    worktimeApi.genericPut(`user/acceptTerms/${termId}`, null, onSuccess, onError);
  }

  updateLastFeatureMessageId(
    module: string,
    lastMessageId: number,
    onSuccess?: SuccessCallback<void>,
    onError?: ErrorCallback,
  ): void {
    worktimeApi.genericPut(
      `user/updateLastFeatureMessageId/${module}/${lastMessageId}`,
      null,
      onSuccess,
      onError,
    );
  }

  updateMyData(
    employee: Partial<Employee>,
    onSuccess?: SuccessCallback<void>,
    onError?: ErrorCallback,
  ): void {
    worktimeApi.genericPut("user/updateMyData", employee, onSuccess, onError);
  }

  registerDevice(
    device: Partial<Device>,
    onSuccess?: SuccessCallback<Device>,
    onError?: ErrorCallback,
  ): void {
    worktimeApi.genericPost(
      "user/device",
      device,
      onSuccess ?? (() => undefined),
      onError ?? (() => undefined),
    );
  }

  getWorkplaces(
    onSuccess: SuccessCallback<WorkPlace[]>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet("user/workplaces", onSuccess, onError);
  }

  getDepartments(
    onSuccess: SuccessCallback<Department[]>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet("user/staff", onSuccess, onError);
  }

  getProjects(
    onSuccess: SuccessCallback<Project[]>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet<ResultListDTO<Project>>(
      "user/projects/0/999999/%20",
      (response) => {
        onSuccess(response?.entities ?? []);
      },
      onError,
    );
  }
}

export const userApi = new UserAPI();
export default userApi;
